import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment, countryCd, cacheLimitedStartDate, getUserProfile } from './util.service';
import { AppointmentBookingSource, AppointmentType, IAppointment, IAppointmentSummary, IResponse, IVehicle } from '@signal/asp-data-commons';
import { DealerState } from '../../store/dealer/dealer.reducer';
import { Store } from '@ngrx/store';
import { selectDealerCode } from '../../store/dealer/dealer.selectors';
import { ServiceHistory } from '../models/service-history';
import { DateTime } from 'luxon';
import _ from 'lodash';
import { SourceOfApptChecklist } from '@signal/asp-data-commons/lib/enums/enum';

@Injectable({
  providedIn: 'root'
})
export class AppointmentService {
  private aspAppointmentApi: string;
  private userName: string;
  private userRoles: [];
  customerId: string;
  vehicles: IVehicle[] = [];
  dateFormat = 'yyyy-MM-dd';

  constructor(private http: HttpClient,
    private dealerState: Store<DealerState>) {
    this.aspAppointmentApi = environment.aspAppointmentApi;
  }

  getUriPathFromApptType(apptType: AppointmentType) {
    return apptType.toLowerCase().replace(' ', '');
  }

  /**
   * Creates the appointment in the backend
   *
   * @param appointment appointment to be created
   */
  create(appointment: IAppointment): Observable<IResponse<IAppointment>> {
    const apptType = this.getUriPathFromApptType(appointment.appointmentType);
    if(appointment["appointmentBookingSource"] !== AppointmentBookingSource.SIGNAL_CTA) {
      appointment.appointmentBookingSource = AppointmentBookingSource.ASPBDC;
    }
    appointment.appointmentCreatedBy = this.userName;
    appointment.appointmentCreatedRoles = this.userRoles;
    appointment.appointmentCreatedDate = DateTime.now().toFormat(this.dateFormat);
    const name = getUserProfile()?.name;
    appointment.appointmentCreatedByUserName = `${name?.firstName || ''} ${name?.lastName || ''}`;
    // appointment.vehicle.make = _.startCase(_.toLower(environment.brand));
    if (appointment.vehicle?.ppmEligible) {
      appointment.dealershipApptComments += ': Eligible for Protection Products - YES';
    }
    if (appointment.vehicle?.protectionProducts && appointment.vehicle?.protectionProducts.length > 0) {
      appointment.dealershipApptComments = appointment.dealershipApptComments + ' : Valid Protection Plans - ' + appointment.vehicle.protectionProducts;
    }
    /* Remove emailAdrress or phoneNumber if empty while creating testdrive appt */
    if(appointment.appointmentType === AppointmentType.TEST_DRIVE || appointment.appointmentType === AppointmentType.VEHICLE_INQUIRY || appointment.appointmentType === AppointmentType.DELIVERY)
    {
      this.removeEmptyKeys(appointment);
    }
    if(appointment.appointmentType === AppointmentType.DELIVERY) {
      appointment.sourceOfReq = SourceOfApptChecklist.DEL_APPT_UI;
    }
    // TODO: Appending country code temporarily.
    // appointment.customer.phoneNumber = `+1-${appointment.customer.phoneNumber}`;

    return this.dealerState.select(selectDealerCode).pipe(switchMap(code => {
     
      return this.http.post(`${(this.aspAppointmentApi)}appointment/${code}/${apptType}`, appointment).pipe(
        map((data: IResponse<IAppointment>) => {
          return data;
        }
        )
      );
    }));

  }

  /**
   * Search the appointments by given search text and type
   *
   * @param query query string to be searched for getting the appointments
   */
  search(query: string): Observable<IResponse<IAppointment[]>> {
    const c = `${new Date().getTime()}`;
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
      return this.http.get(`${(this.aspAppointmentApi)}appointment/${dealerCode}/customer/search`, {
        params: { query, c }
      }).pipe(
        map((data: IResponse<IAppointment[]>) => {
          return data;
        }
        )
      );
    }));
  }

  /**
   * Cancel the appointment in the backend
   *
   * @param appointment appointment to be cancelled
   */
  cancel(appointment: IAppointment): Observable<IResponse<IAppointment>> {
    const apptType = this.getUriPathFromApptType(appointment.appointmentType);
    const dealerCode = appointment.dealerCode;
    return this.http.delete(`${(this.aspAppointmentApi)}appointment/${dealerCode}/${apptType}/${appointment.appointmentId}?sourceOfDeletion=${AppointmentBookingSource.ASPBDC}`).pipe(
      map((data: IResponse<IAppointment>) => {
        return data;
      }
      )
    );
  }

  /**
   * update the appointment in the backend
   *
   * @param appointment the appointment to be updated
   */
  update(appointment: IAppointment): Observable<IResponse<IAppointment>> {
    const apptType = this.getUriPathFromApptType(appointment.appointmentType);
    const dealerCode = appointment.dealerCode;
    if(appointment.appointmentType !== AppointmentType.VEHICLE_INQUIRY) {
      if(appointment.appointmentType === AppointmentType.LEARNING && this.checkFormakeandYearForLearning(appointment)){
        appointment.vehicle.make = 'other'
        appointment.vehicle.year = null
      } 
      }
    appointment.appointmentBookingSource = AppointmentBookingSource.ASPBDC;
    appointment.appointmentCreatedBy = this.userName;
    appointment.appointmentCreatedRoles = this.userRoles;
    appointment.appointmentCreatedDate = DateTime.now().toFormat(this.dateFormat);

    // TODO: Appending country code temporarily.
    // appointment.customer.phoneNumber = `+1-${appointment.customer.phoneNumber}`;

    /* Removing forbidden properties for update */
    ['dealer', 'dealerCode', 'stockNumber'].forEach(p => delete appointment[p]);

    /* Remove emailAdrress or phoneNumber if empty while updating testdrive appt */
    if(appointment.appointmentType === AppointmentType.TEST_DRIVE || appointment.appointmentType === AppointmentType.VEHICLE_INQUIRY || appointment.appointmentType === AppointmentType.DELIVERY)
    {
     this.removeEmptyKeys(appointment);
    }
    if(appointment.appointmentType === AppointmentType.DELIVERY) {
      appointment.sourceOfReq = SourceOfApptChecklist.DEL_APPT_UI;
    }
    return this.http.put(`${(this.aspAppointmentApi)}appointment/${dealerCode}/${apptType}/${appointment.appointmentId}`, appointment).pipe(
      map((data: IResponse<IAppointment>) => {
        return data;
      }
      )
    );
  }

  checkFormakeandYearForLearning(appointment){
    return (!appointment.vehicle?.make || (appointment?.vehicle?.make.toLocaleLowerCase() === 'other' && (!appointment?.vehicle?.year || appointment?.vehicle?.year==='undefined')))
  }

  resyncAppointment(appointment: any): Observable<IAppointment> {
    const apptType = this.getUriPathFromApptType(appointment.appointmentType);
    const dealerCode = appointment.dealerCode;
    return this.http.patch(`${(this.aspAppointmentApi)}appointment/${dealerCode}/${apptType}/resync/${appointment.appointmentId}`, null).pipe(
      map((data: IResponse<IAppointment>) => {
        return data.data;
      }
      )
    );
  }

  confirmAppointment(appointment: IAppointment): Observable<IAppointment> {
    const apptType = this.getUriPathFromApptType(appointment.appointmentType);
    const dealerCode = appointment.dealerCode;
    return this.http.patch(`${(this.aspAppointmentApi)}appointment/${dealerCode}/confirmAppointment/${appointment.appointmentId}`, appointment).pipe(
      map((data: IResponse<IAppointment>) => {
        return data.data;
      }
      )
    );
  }

  updateTimeSlot(appointment: any): Observable<IAppointment> {
    const apptType = this.getUriPathFromApptType(appointment.appointmentType);
    const dealerCode = appointment.dealerCode;
    appointment.serviceRequests = {};
    appointment.appointmentCreatedBy = this.userName;
    appointment.appointmentCreatedRoles = this.userRoles;
    appointment.appointmentCreatedDate = DateTime.now().toFormat(this.dateFormat);

    return this.http.patch(`${(this.aspAppointmentApi)}appointment/${dealerCode}/${apptType}/${appointment.appointmentId}`, appointment).pipe(
      map((data: IResponse<IAppointment>) => {
        return data.data;
      }
      )
    );
  }

  /**
   * get the appointment
   *
   * @param apptType Appointment type (either SERVICE, TEST_DRIVE, DELIVERY)
   * @param appointmentId Appointment Id to be get from backend
   */
  get(apptType: AppointmentType, appointmentId: string): Observable<IResponse<IAppointment>> {
    const apptTypeUri = this.getUriPathFromApptType(apptType);
    return this.dealerState.select(selectDealerCode).pipe(switchMap(code => {
      return this.http.get(`${(this.aspAppointmentApi)}appointment/${code}/${apptTypeUri}/${appointmentId}`).pipe(
        map((data: IResponse<IAppointment>) => {
          // data.data.customer.phoneNumber = data.data.customer.phoneNumber.replace('+1-', '');
          return data;
        }))
    }));

  }

  getVehicles(dealerCode: string, customerId: string) {
    if (this.customerId === customerId) {
      return new Observable((observer) => {
        observer.next(this.vehicles);
        observer.complete();
      });
    } else {
      return this.http.get(`${(this.aspAppointmentApi)}consumer/${dealerCode}/${customerId}/vehicles?countryCd=${countryCd}`).pipe(
        map((data: IResponse<IVehicle[]>) => {
          this.customerId = customerId;
          this.vehicles = data.data
          return data.data;
        })
      );
    }
  }

  sendLink(appointmentIds:string[]){
    return this.dealerState.select(selectDealerCode).pipe(switchMap(code => {
      return this.http.post(`${(this.aspAppointmentApi)}appointment/${code}/send-mail-to-customer`,{
        appointmentIds : appointmentIds
      }).pipe(
        map((data: any) => {
          return data;
        }))
    }));
  }

  getVehicleServiceHistory(vin: string): Observable<ServiceHistory> {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
      return this.http.get(`${(this.aspAppointmentApi)}vehicle/${dealerCode}/${vin}/servicehistory`).pipe(
        map((data: ServiceHistory) => {
          return data;
        })
      );
    }));
  }

  getAppointmentSummary(department: string, startDate: string, endDate: string, filters: any, viewMode: string): Observable<IResponse<IAppointmentSummary[]>> {
    const size = 1000;
    const status = filters.status && filters.status !== 'All Status' ? filters.status : (viewMode === 'Week') ? 'Completed,Scheduled' : 'Canceled,Completed,Scheduled';
    const advisorIdQuery = filters.advisorId && filters.advisorId !== 'ALL' ? `&advisorId=${filters.advisorId}` : '';
    const transportQuery = filters.transportId && filters.transportId !== 'ALL' ? `&transport=${filters.transportId}` : '';
    const teamIdQuery = '';
    const appointmentSalesQuery = department === 'SALES' ? (filters.appointmentType && filters.appointmentType !== 'All Types' ? `&appointmentType=${filters.appointmentType}` : '') : '';
    const appointmentSourceQuery = filters.bookingSource && filters.bookingSource !== 'All Appointment Sources' ? `&bookingSource=${filters.bookingSource}` : '';
    const vinQuery = filters.vin ? `&vin=${filters.vin}` : '';

    const queryString = `?startDate=${startDate}&endDate=${endDate}&status=${status}&size=${size}${advisorIdQuery}${transportQuery}${teamIdQuery}${appointmentSalesQuery}${vinQuery}${appointmentSourceQuery}`;
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
      return this.http.get(`${(this.aspAppointmentApi)}appointment/${dealerCode}/${department}/summary${queryString}`).pipe(
        map((data: IResponse<IAppointmentSummary[]>) => {
          return data;
        })
      );
    }));
  }
  setUserName(userName) {
    this.userName = userName;
  }

  setUserRoles(userRoles) {
    const allowedRoles = ["DD_ASP","DD_ServiceManagement","DD_TIS","DD_NewVehicleVMSIncludeCost"]
    this.userRoles = userRoles.filter(role => allowedRoles.includes(role));
  }

  getPendingAppointments(type: string, offset: number, limit: number, sortingField: string, sortOrder: string, isSearch: boolean, searchConfig?: any ): Observable<IResponse<any>> {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
      if(type === 'pending') {
      let searchQuery = '';
      if(isSearch) {
        searchQuery += searchConfig?.customerNameFilter ? `&customerName=${searchConfig?.customerNameFilter.replace(/ /gi, '')}` : '';
        searchQuery += searchConfig?.contactInfoFilter ? `&contactInfo=${searchConfig?.contactInfoFilter.replace(/ /gi, '')}` : '';
        searchQuery += searchConfig?.modelFilter ? `&model=${searchConfig?.modelFilter.replace(/ /gi, '')}` : '';
        searchQuery += searchConfig?.vinFilter ? `&vin=${searchConfig?.vinFilter.replace(/ /gi, '')}` : '';
        searchQuery += searchConfig?.appointmentCreatedDateFilter ? `&appointmentCreatedDate=${searchConfig?.appointmentCreatedDateFilter}` : '';
        searchQuery += searchConfig?.appointmentTypeFilter ? `&appointmentType=${searchConfig?.appointmentTypeFilter.replace(/ /gi, '')}` : '';
        searchQuery += searchConfig?.sourceFilter ? `&source=${searchConfig?.sourceFilter.replace(/ /gi, '')}` : '';
        searchQuery += searchConfig?.vehicleStatusFilter ? `&vehicleStatus=${searchConfig?.vehicleStatusFilter.replace(/ /gi, '')}` : '';
        searchQuery += searchConfig?.dealerFilter ? `&dealer=${searchConfig?.dealerFilter.replace(/ /gi, '')}` : '';
        searchQuery += searchConfig?.linkSentOnFilter ? `&linkSentOn=${searchConfig?.linkSentOnFilter}` : '';
        searchQuery += searchConfig?.salesConsultantFilter ? `&advisor=${searchConfig?.salesConsultantFilter.replace(/ /gi, '')}` : '';
        searchQuery += searchConfig?.appointmentStartDateFilter ? `&appointmentCreatedDate=${searchConfig?.appointmentStartDateFilter}` : '';
        searchQuery += searchConfig?.commentsFilter ? `&comments=${searchConfig?.commentsFilter.replace(/ /gi, '')}` : '';
      }
      return this.http.get(`${this.aspAppointmentApi}appointment/${dealerCode}/pending-appointments?offset=${offset}&limit=${limit}&sortingField=${sortingField}&sortOrder=${sortOrder}${searchQuery}`).pipe(
        map((data: IResponse<any>) => {
          return data;
        })
      )
      }
      else {
        return this.http.get(`${this.aspAppointmentApi}appointment/${dealerCode}/testdrive-delete-pending`).pipe(
          map((data: IResponse<any>) => {
            return data;
          })
        )
      }
    }));
  }

  removeEmptyKeys(appointment: any) {
    if(appointment.customer.emailAddress ==='')
    {
      delete appointment.customer.emailAddress;
      delete appointment.emailAddress;
    }
    else if(appointment.customer.phoneNumber ==='')
    {
      delete appointment.customer.phoneNumber;
    }
    if(appointment.appointmentType === AppointmentType.DELIVERY){
      if(appointment.customer.firstName === ''){
        delete appointment.customer.firstName
      }
      if(appointment.customer.emailAddress ==='')
      {
        delete appointment.customer.emailAddress;
        delete appointment.emailAddress;
      }
      if(appointment.customer.phoneNumber ==='')
      {
        delete appointment.customer.phoneNumber;
      }
    }
  }
}
